import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Lock } from "lucide-react";
import { MISSIONS, isMissionLocked } from "../content/missions";
import { shouldHandleMenuKey } from "../lib/menuKeys";

const CORE_ACCENT = "#6B9DD3";
const CAMPAIGN_ACCENT = "#E8692E";
const LEGACY_ACCENT = "#D4AF37";

const MENU = [
  { key: "core", to: "/core" },
  { key: "campaign", to: "/campaigns" },
  { key: "legacy", to: "/legacy" },
];

// The landing screen after ENTER: a game-style main menu. Arrow keys move the
// cursor, Enter selects, hover/tap works as normal links.
export default function Home() {
  const navigate = useNavigate();
  const [selected, setSelected] = useState(0);
  const refs = useRef([]);
  const menuRef = useRef(null);

  const move = useCallback(
    (delta) => {
      const next = (selected + delta + MENU.length) % MENU.length;
      setSelected(next);
      refs.current[next]?.focus();
    },
    [selected]
  );

  useEffect(() => {
    const onKey = (e) => {
      if (!shouldHandleMenuKey(e, menuRef.current)) return;
      if (e.key === "ArrowRight" || e.key === "ArrowDown") {
        e.preventDefault();
        move(1);
      } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
        e.preventDefault();
        move(-1);
      } else if (e.key === "Enter" && e.target === document.body) {
        navigate(MENU[selected].to);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [move, navigate, selected]);

  const itemProps = (idx) => ({
    ref: (el) => (refs.current[idx] = el),
    to: MENU[idx].to,
    onMouseEnter: () => setSelected(idx),
    onFocus: () => setSelected(idx),
    "data-selected": selected === idx,
  });

  return (
    <div className="bg-[#06080C] text-white">
      <section
        data-testid="main-menu"
        className="relative min-h-[88vh] overflow-hidden grain dusky-sky dusky-clouds"
      >
        <div className="absolute inset-0">
          <img
            src="/assets/splash-bg.webp"
            alt=""
            width={1376}
            height={768}
            loading="eager"
            fetchPriority="high"
            decoding="async"
            className="h-full w-full object-cover object-center opacity-[0.12]"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-[#06080C]/60 via-[#06080C]/80 to-[#06080C]" />
        <div className="absolute inset-0 scanlines pointer-events-none" />

        <div ref={menuRef} className="relative z-10 px-5 md:px-12 pt-8 md:pt-12 pb-24 max-w-6xl mx-auto">
          {/* HUD line */}
          <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-[0.3em] text-[#8A91A0]">
            <div className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 bg-[#D4AF37] hud-dot" />
              AEGIS // Main Menu
            </div>
            <div className="hidden md:block">Connection: Established</div>
          </div>

          {/* Title */}
          <div className="text-center mt-10 md:mt-14 mb-10 md:mb-12 fade-up">
            <h1
              data-testid="main-menu-heading"
              className="font-display text-5xl sm:text-6xl md:text-7xl uppercase tracking-[0.2em] etched leading-none"
            >
              Select Mode
            </h1>
            <div className="flex items-center justify-center gap-3 mt-5">
              <div className="h-px w-12 md:w-24 bg-[#D4AF37]" />
              <div className="font-mono uppercase tracking-[0.4em] text-[#D4AF37] text-[10px] md:text-xs">
                Strength in Order
              </div>
              <div className="h-px w-12 md:w-24 bg-[#D4AF37]" />
            </div>
          </div>

          {/* Modes */}
          <div className="grid md:grid-cols-2 gap-5 md:gap-6">
            <ModeCard
              {...itemProps(0)}
              testId="mode-core"
              index="01"
              accent={CORE_ACCENT}
              cornersCls="corners-steel"
              title="Core"
              subtitle="The Standard"
              blurb="Tactical. Clean. Disciplined."
              cta="Enter Core"
              ctaBoxed
              bgImage="/assets/mode-core.webp"
              bgSize={[1200, 1200]}
              bgPosition="70% 45%"
            />

            <ModeCard
              {...itemProps(1)}
              testId="mode-campaign"
              index="02"
              accent={CAMPAIGN_ACCENT}
              cornersCls="corners"
              title="Campaign"
              subtitle="Mission Files"
              blurb="Stories. Units. Experiences."
              cta="Enter Campaign"
              bgImage="/assets/mode-campaign.webp"
              bgSize={[1400, 788]}
              bgPosition="68% 50%"
            >
              <ul className="w-full max-w-sm space-y-2 font-mono text-[10px] md:text-[11px] uppercase tracking-[0.25em]">
                {MISSIONS.map((m) => {
                  const locked = isMissionLocked(m);
                  return (
                    <li
                      key={m.slug}
                      className="flex items-center gap-3 border border-[#1F2330] bg-[#06080C]/85 backdrop-blur-[2px] px-3 py-2"
                    >
                      <span style={{ color: locked ? "#8A91A0" : m.accent }}>{m.code}</span>
                      <span className={`flex-1 truncate ${locked ? "text-[#8A91A0]" : "text-white"}`}>
                        {locked ? "████████" : m.name}
                      </span>
                      {locked && <Lock className="w-3 h-3 text-[#8A91A0]" />}
                    </li>
                  );
                })}
              </ul>
            </ModeCard>
          </div>

          {/* Restricted: Legacy */}
          <Link
            {...itemProps(2)}
            data-testid="mode-legacy"
            className="group relative mt-5 md:mt-6 block border border-dashed border-[#5C4A1A] hover:border-[#D4AF37] data-[selected=true]:border-[#D4AF37] bg-[#0A0D14]/90 overflow-hidden transition-colors outline-none focus-visible:ring-1 focus-visible:ring-[#D4AF37]"
          >
            <div className="absolute inset-0 warning-stripes opacity-40" />
            <div className="relative px-5 md:px-8 py-5 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
              <div className="w-11 h-11 shrink-0 border border-[#D4AF37]/60 bg-[#06080C] flex items-center justify-center">
                <Lock className="w-5 h-5" style={{ color: LEGACY_ACCENT }} />
              </div>
              <div className="flex-1">
                <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-[#C54A4A]">
                  Restricted Access
                </div>
                <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 mt-1">
                  <span className="font-display text-2xl md:text-3xl uppercase tracking-[0.15em] etched">
                    Legacy
                  </span>
                  <span className="font-mono text-[10px] md:text-xs uppercase tracking-[0.35em] text-[#D4AF37]">
                    Issued. Not Sold.
                  </span>
                </div>
              </div>
              <div className="inline-flex items-center gap-2 font-mono uppercase text-[10px] tracking-[0.3em] text-[#A0A6B5] group-hover:text-[#D4AF37] group-data-[selected=true]:text-[#D4AF37] transition-colors">
                View Clearance <ArrowRight className="w-3 h-3" />
              </div>
            </div>
          </Link>

          {/* Controls hint */}
          <div className="mt-8 flex items-center justify-center gap-4 text-[9px] md:text-[10px] font-mono uppercase tracking-[0.3em] text-[#8A91A0]">
            <span className="hidden md:inline-flex items-center gap-2">
              <Key>←</Key>
              <Key>→</Key>
              Navigate
            </span>
            <span className="hidden md:inline-flex items-center gap-2">
              <Key>Enter</Key>
              Select
            </span>
            <span className="md:hidden">Tap a mode to begin</span>
          </div>
        </div>

        {/* Bottom marquee */}
        <div className="absolute bottom-0 left-0 right-0 border-y border-[#1F2330] bg-[#06080C]/90 overflow-hidden z-10">
          <div className="flex animate-marquee whitespace-nowrap py-3">
            {Array.from({ length: 2 }).map((_, k) => (
              <div key={k} className="flex shrink-0" aria-hidden={k > 0}>
                {[
                  "BUILT ON DISCIPLINE",
                  "UNITED AS ONE",
                  "HOLD THE LINE",
                  "ISSUED · NOT SOLD",
                  "STRENGTH IN ORDER",
                  "BUILT BY THE LINE · FOR THE LINE",
                ].map((t, j) => (
                  <span
                    key={j}
                    className="font-display text-lg md:text-xl uppercase tracking-[0.15em] mx-8 text-white/60"
                  >
                    {t} <span className="text-[#D4AF37] mx-2">✦</span>
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

const ModeCard = React.forwardRef(function ModeCard(
  {
    testId,
    index,
    accent,
    cornersCls,
    title,
    subtitle,
    blurb,
    cta,
    ctaBoxed = false,
    bgImage,
    bgSize = [1200, 1200],
    bgPosition = "center",
    children,
    ...linkProps
  },
  ref
) {
  return (
    <Link
      ref={ref}
      data-testid={testId}
      {...linkProps}
      className={`group relative block bg-[#11141C]/95 border border-[#1F2330] hover:border-[var(--accent)] data-[selected=true]:border-[var(--accent)] overflow-hidden ${cornersCls} transition-all duration-300 hover:-translate-y-1 shadow-[0_14px_40px_rgba(0,0,0,0.35)] outline-none focus-visible:ring-1 focus-visible:ring-[var(--accent)]`}
      style={{ "--accent": accent }}
    >
      {bgImage ? (
        <>
          <img
            src={bgImage}
            alt=""
            width={bgSize[0]}
            height={bgSize[1]}
            loading="eager"
            decoding="async"
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105 group-data-[selected=true]:scale-[1.03]"
            style={{ objectPosition: bgPosition }}
          />
          {/* Darken the left/top so the copy stays readable over the photo. */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#06080C]/90 via-[#06080C]/35 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#06080C]/75 via-transparent to-[#06080C]/20" />
          <div
            className="absolute inset-0 opacity-40 group-hover:opacity-70 group-data-[selected=true]:opacity-70 transition-opacity mix-blend-screen"
            style={{ background: `radial-gradient(circle at 75% 30%, ${accent}33 0%, transparent 60%)` }}
          />
        </>
      ) : (
        <>
          <div className="absolute inset-0 brushed opacity-50" />
          <div
            className="absolute inset-0 opacity-60 group-hover:opacity-100 group-data-[selected=true]:opacity-100 transition-opacity"
            style={{ background: `radial-gradient(circle at 70% 30%, ${accent}2E 0%, transparent 65%)` }}
          />
        </>
      )}
      <div className="relative p-6 md:p-8 min-h-[420px] md:min-h-[460px] flex flex-col [text-shadow:0_2px_12px_rgba(0,0,0,0.85)]">
        <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.3em]">
          <span style={{ color: accent }}>Mode {index}</span>
          <span
            className="opacity-0 group-hover:opacity-100 group-data-[selected=true]:opacity-100 transition-opacity"
            style={{ color: accent }}
            aria-hidden="true"
          >
            ▶ Selected
          </span>
        </div>
        <h2 className="font-display text-5xl md:text-6xl uppercase leading-none tracking-[0.12em] etched-steel mt-4">
          {title}
        </h2>
        <div className="font-mono uppercase tracking-[0.35em] text-xs mt-3" style={{ color: accent }}>
          {subtitle}
        </div>
        <p className="mt-2 text-[#C9CED8] text-sm">{blurb}</p>

        <div className="my-6 flex-1 flex items-end">{children}</div>

        <div
          className={`self-start inline-flex items-center gap-3 font-mono uppercase text-xs tracking-[0.3em] group-hover:gap-5 group-data-[selected=true]:gap-5 transition-all ${
            ctaBoxed ? "border border-[var(--accent)] bg-[#06080C]/80 px-5 py-3 corners corners-steel" : ""
          }`}
          style={{ color: accent }}
        >
          {cta} <ArrowRight className="w-4 h-4" />
        </div>
      </div>
    </Link>
  );
});

function Key({ children }) {
  return (
    <span className="border border-[#2A3040] bg-[#11141C] px-1.5 py-0.5 text-[#A0A6B5]">{children}</span>
  );
}
